import { measureLatency } from '../../../gctl-measure';
import {
  LiveInteractionRoomEmbedCodeFields,
  LiveInteractionRoomEmbedCode,
} from '../../../models/components/schemas/live-interaction-room-embed-code/LiveInteractionRoomEmbedCode';
import {
  Select,
  NetworkError,
  deepCamelCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetInteractionRoomRoomIdEmbedCodeFields = readonly LiveInteractionRoomEmbedCodeFields[];

/** Options for GET /interaction_rooms/{room_type}/{room_id}/embed_code */
export type GetInteractionRoomRoomIdEmbedCodeOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.live.interaction_room_embed_code */
  select: Paths;
  where: {
    /**
     * The type of the interaction room.
     *
     * Option descriptions:
     *  * `live_event` - The room type is an event.
     *  * `venue` - The room type is a venue.
     *
     */
    roomType: `live_event` | `venue`;
    /**
     * The ID of the interaction room.
     */
    roomId: string;
  };

  /** Possible query parameters for /interaction_rooms/{room_type}/{room_id}/embed_code */
  query: {
    /**
     * The height of the embed code contents. This parameter is ignored when **responsive** is `true`.
     */
    height?: number;

    /**
     * Whether the header is disabled for the interaction room.
     */
    isHeaderDisabled?: boolean;

    /**
     * Whether the management feature is disabled for the interaction room.
     */
    isManagementDisabled?: boolean;

    /**
     * The enabled module of the interaction room.
     *
     * Option descriptions:
     *  * `auto` - The enabled module is determined by the client.
     *  * `chat` - The chat module is enabled.
     *  * `poll` - The poll module is enabled.
     *  * `qna` - The Q&A module is enabled.
     *
     */
    module?: `auto` | `chat` | `poll` | `qna`;

    /**
     * Whether the embed code contents are rendered using a fixed width and height.
     */
    responsive?: boolean;

    /**
     * The graphical theme of the interaction room's modules.
     *
     * Option descriptions:
     *  * `dark` - The room's modules use the dark theme.
     *  * `light` - The room's modules use the light theme.
     *
     */
    theme: `dark` | `light`;

    /**
     * The width of the embed code contents. This parameter is ignored when **responsive** is `true`.
     */
    width?: number;
  };
};

/** Result type for GET /interaction_rooms/{room_type}/{room_id}/embed_code */
export type GetInteractionRoomRoomIdEmbedCodeResult<Paths extends readonly string[]> = Select<
  LiveInteractionRoomEmbedCode,
  Paths
>;

/**
 * Get the HTML embed code for an interaction room
 * This method returns the HTML embed code and related data for the specified interaction room.
 */
export async function getInteractionRoomRoomIdEmbedCode<
  Paths extends GetInteractionRoomRoomIdEmbedCodeFields,
>({
  baseUrl,
  select,
  where: { roomType, roomId },
  query,
  ...options
}: GetInteractionRoomRoomIdEmbedCodeOptions<Paths>): Promise<
  GetInteractionRoomRoomIdEmbedCodeResult<Paths>
> {
  return measureLatency('getInteractionRoomRoomIdEmbedCode', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/interaction_rooms/${roomType}/${roomId}/embed_code?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetInteractionRoomRoomIdEmbedCodeResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
